import { useMemo } from 'react'

import {
  useFilters, useModals, useNumericParams, useSetState,
} from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditCampaignQuoteTemplateModal from '@modals/CreateOrEditCampaignQuoteTemplateModal'

import type { AgencyModel, CampaignQuoteTemplateModel, DefaultQuoteItemModel } from '@models/types'

import CreateOrEditDefaultQuoteItemModal from '@modals/CreateOrEditDefaultQuoteItemModal'
import NewDefaultQuoteItemModal from '@modals/NewDefaultQuoteItemModal'
import NewOrderModal from '@modals/NewOrderModal'
import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showCreateOrEditCampaignQuoteTemplateModal: () => void,
    showCreateOrEditDefaultQuoteItemModal: () => void,
    showNewDefaultQuoteItemModal: () => void,
    showNewOrderModal: () => void,
  },
}

const defaultState = {
  showCreateOrEditCampaignQuoteTemplateModal: false,
  showCreateOrEditDefaultQuoteItemModal: false,
  showNewDefaultQuoteItemModal: false,
  showNewOrderModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditCampaignQuoteTemplateModal: {
      closeModal: () => setState({ showCreateOrEditCampaignQuoteTemplateModal: false }),
      createCampaignQuoteTemplate: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateCampaignQuoteTemplate: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditDefaultQuoteItemModal: {
      closeModal: () => setState({
        showCreateOrEditDefaultQuoteItemModal: false,
        showNewDefaultQuoteItemModal: false,
      }),
      createDefaultQuoteItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateDefaultQuoteItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    NewDefaultQuoteItemModal: {
      closeModal: () => setState({ showNewDefaultQuoteItemModal: false }),
    },
    NewOrderModal: {
      addItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({
        showNewDefaultQuoteItemModal: false,
        showNewOrderModal: false,
      }),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const { agencyId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditCampaignQuoteTemplateModal,
    showCreateOrEditDefaultQuoteItemModal,
    showNewDefaultQuoteItemModal,
    showNewOrderModal,
  } = state

  const filtersPayload = useFilters({ persistFilters: true })
  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
    topModalKey,
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showCreateOrEditCampaignQuoteTemplateModal: (payload: { campaignQuoteTemplate: CampaignQuoteTemplateModel }) => {
        setModalData('CreateOrEditCampaignQuoteTemplateModal', payload)
        setState({ showCreateOrEditCampaignQuoteTemplateModal: true })
      },
      showCreateOrEditDefaultQuoteItemModal: (payload: { defaultQuoteItem: DefaultQuoteItemModel }) => {
        setModalData('CreateOrEditDefaultQuoteItemModal', payload)
        setState({ showCreateOrEditDefaultQuoteItemModal: true })
      },
      showNewDefaultQuoteItemModal: (payload: { agency: AgencyModel }) => {
        setModalData('NewDefaultQuoteItemModal', payload)
        setState({ showNewDefaultQuoteItemModal: true })
      },
      showNewOrderModal: (payload: { agency: AgencyModel }) => {
        setModalData('NewOrderModal', payload)
        setState({ showNewOrderModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt, topModalKey]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent agencyId={agencyId} pageFilters={pageFilters} />

        <CreateOrEditCampaignQuoteTemplateModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditCampaignQuoteTemplateModal', setState)}
          showModal={showCreateOrEditCampaignQuoteTemplateModal}
        />

        <CreateOrEditDefaultQuoteItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditDefaultQuoteItemModal', setState)}
          showModal={showCreateOrEditDefaultQuoteItemModal}
        />

        <NewDefaultQuoteItemModal
          callbacks={callbacks('NewDefaultQuoteItemModal', setState)}
          showModal={showNewDefaultQuoteItemModal}
        />

        <NewOrderModal
          callbacks={callbacks('NewOrderModal', setState)}
          showModal={showNewOrderModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
